import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Plus, Trash2, Pencil, Check, ChevronDown, ChevronRight,
  Play, CheckCircle, Pause, Paperclip, FileText, Calendar, Users, Wrench,
  AlertTriangle, Box, UserPlus, Layers, List, LayoutGrid, Clock
} from 'lucide-react'
import api from '../../../core/services/api'
import { Toast, ConfirmModal } from '../../../components/Toast'
import { AllegatiModal } from '../../../components/allegati/AllegatiModal'
import FaseAllegatiModal from '../../../components/allegati/FaseAllegatiModal'
import { OwnershipBadge } from '../../../components/OwnershipBadge'
import { TransferOwnershipModal } from '../../../components/TransferOwnershipModal'
import { GanttChart } from '../../../components/commesse/GanttChart'
import SchedePerEntita from '../../../components/schede/SchedePerEntita'
import RendicontazionePanel from '../../../components/rendicontazione/RendicontazionePanel'
import {
  CommessaDetail, StatoCommessa, FaseDetail, FaseForm, GiornoFaseForm,
  PersonaleMinimal, StrumentoMinimal, ArticoloCommessa
} from '../../../types/commesse'

// ========== COSTANTI ==========

const FASE_TITOLI = [
  'Acquisizione e analisi requisiti',
  'Progettazione preliminare',
  'Progettazione esecutiva e distinta materiali',
  'Approvvigionamenti e logistica',
  'Produzione interna e controlli qualità',
  'Installazione in loco',
  'Collaudi e certificazioni',
  'Consegna finale e documentazione',
  'Assistenza post-vendita',
]

const ARTICOLO_COLORS = [
  { bar: '#60a5fa', fill: '#2563eb', light: '#dbeafe', text: '#1d4ed8' },
  { bar: '#a78bfa', fill: '#7c3aed', light: '#ede9fe', text: '#6d28d9' },
  { bar: '#34d399', fill: '#059669', light: '#d1fae5', text: '#047857' },
  { bar: '#fb923c', fill: '#ea580c', light: '#ffedd5', text: '#c2410c' },
  { bar: '#f472b6', fill: '#db2777', light: '#fce7f3', text: '#be185d' },
  { bar: '#fbbf24', fill: '#d97706', light: '#fef3c7', text: '#b45309' },
  { bar: '#2dd4bf', fill: '#0d9488', light: '#ccfbf1', text: '#0f766e' },
  { bar: '#94a3b8', fill: '#475569', light: '#f1f5f9', text: '#334155' },
]

// ========== GANTT ARTICOLI ==========

const ROW_H = 44

function GanttArticoli({
  articoli, dataInizio,
  onReorder, onDurataChange,
}: {
  articoli: ArticoloCommessa[]
  dataInizio: string | null
  onReorder?: (orderedIds: string[]) => void
  onDurataChange?: (articoloId: string, newDurata: number) => void
}) {
  const baseBars = articoli
    .filter(art => (art.fasi ?? []).length > 0)
    .map((art, idx) => {
      const fasi = art.fasi ?? []
      const computed = fasi.reduce((s, f) => s + f.durata_prevista, 0) || 5
      const durata = art.durata_gantt ?? computed
      const completate = fasi.filter(f => f.completata).length
      return { art, fasi, durata, completate, idx }
    })

  const dragRef = useRef<{
    type: 'resize' | 'reorder'
    artId: string; artIndex: number
    startX: number; startY: number; startDurata: number
  } | null>(null)
  const [resizePreview, setResizePreview] = useState<{ artId: string; durata: number } | null>(null)
  const [reorderPreview, setReorderPreview] = useState<{ from: number; to: number } | null>(null)
  const totalDays = baseBars.reduce((s, b) => s + b.durata, 0) || 1
  const dayWidth = Math.max(20, Math.min(50, 700 / totalDays))

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current) return
      const { type, startX, startY, artIndex } = dragRef.current
      if (type === 'resize') {
        const delta = Math.round((e.clientX - startX) / dayWidth)
        setResizePreview({ artId: dragRef.current.artId, durata: Math.max(1, dragRef.current.startDurata + delta) })
      } else {
        const shift = Math.round((e.clientY - startY) / ROW_H)
        setReorderPreview({ from: artIndex, to: Math.max(0, Math.min(baseBars.length - 1, artIndex + shift)) })
      }
    }
    const onUp = (e: MouseEvent) => {
      if (!dragRef.current) return
      const { type, artId, artIndex, startX, startDurata } = dragRef.current
      const curBars = reorderPreview
        ? (() => { const c = [...baseBars]; const [m] = c.splice(reorderPreview.from, 1); c.splice(reorderPreview.to, 0, m); return c })()
        : baseBars
      if (type === 'resize' && onDurataChange) {
        const delta = Math.round((e.clientX - startX) / dayWidth)
        const nd = Math.max(1, startDurata + delta)
        if (nd !== startDurata) onDurataChange(artId, nd)
      } else if (type === 'reorder' && onReorder) {
        const shift = Math.round((e.clientY - dragRef.current.startY) / ROW_H)
        const to = Math.max(0, Math.min(baseBars.length - 1, artIndex + shift))
        if (to !== artIndex) onReorder(curBars.map(b => b.art.id))
      }
      dragRef.current = null
      setResizePreview(null)
      setReorderPreview(null)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp) }
  }, [baseBars, dayWidth, onDurataChange, onReorder, reorderPreview])

  if (baseBars.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 text-center text-gray-400">
        <Layers className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">Nessun articolo con fasi — aggiungi fasi agli articoli per visualizzare il Gantt</p>
      </div>
    )
  }

  const displayBars = reorderPreview
    ? (() => { const c = [...baseBars]; const [m] = c.splice(reorderPreview.from, 1); c.splice(reorderPreview.to, 0, m); return c })()
    : baseBars

  const canDrag = !!onReorder || !!onDurataChange
  let cursor = 0

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden select-none">
      <div className="overflow-x-auto">
        <div className="min-w-fit">
          {/* Header */}
          <div className="flex border-b border-gray-200">
            <div className="w-52 flex-shrink-0 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase">Articolo</div>
            <div className="flex-1 py-2 px-3 bg-gray-50 text-xs font-medium text-gray-500 uppercase">Timeline</div>
          </div>

          {displayBars.map(({ art, fasi, durata, completate, idx }, rowIdx) => {
            const isDragging = dragRef.current?.artId === art.id
            const color = ARTICOLO_COLORS[idx % ARTICOLO_COLORS.length]
            const durataDisplay = resizePreview?.artId === art.id ? resizePreview.durata : durata
            const phaseDone = fasi.length > 0 ? completate / fasi.length : 0
            const isCompleted = fasi.length > 0 && completate === fasi.length
            const barLeft = cursor * dayWidth
            cursor += durataDisplay
            const barWidth = Math.max(durataDisplay * dayWidth, dayWidth)

            return (
              <div
                key={art.id}
                className={`flex border-b border-gray-100 transition-colors ${isDragging ? 'opacity-60 bg-blue-50' : 'hover:bg-gray-50'}`}
                style={{ height: ROW_H }}
              >
                {/* Label drag */}
                <div
                  className={`w-52 flex-shrink-0 px-3 flex items-center gap-2 ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}`}
                  onMouseDown={onReorder ? e => {
                    e.preventDefault()
                    dragRef.current = { type: 'reorder', artId: art.id, artIndex: rowIdx, startX: e.clientX, startY: e.clientY, startDurata: durata }
                  } : undefined}
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color.bar }} />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{art.ordine + 1}. {art.titolo}</p>
                    <p className="text-xs text-gray-400">{completate}/{fasi.length} fasi</p>
                  </div>
                </div>

                {/* Barra */}
                <div className="relative flex-1 py-1" style={{ minWidth: (totalDays + 3) * dayWidth }}>
                  <div className="absolute top-1 rounded h-7" style={{ left: barLeft, width: barWidth, backgroundColor: isCompleted ? '#4ade80' : color.bar, opacity: 0.5 }} />
                  {phaseDone > 0 && (
                    <div className="absolute top-1 rounded h-7" style={{ left: barLeft, width: barWidth * phaseDone, backgroundColor: isCompleted ? '#16a34a' : color.fill, opacity: 0.85 }} />
                  )}
                  <span className="absolute top-2 text-xs font-medium pointer-events-none" style={{ left: barLeft + 6, color: color.fill }}>
                    {durataDisplay}gg{phaseDone > 0 ? ` · ${Math.round(phaseDone * 100)}%` : ''}
                  </span>
                  {onDurataChange && (
                    <div
                      className="absolute top-1 h-7 w-3 cursor-col-resize z-10 flex items-center justify-center group"
                      style={{ left: barLeft + barWidth - 4 }}
                      onMouseDown={e => {
                        e.preventDefault(); e.stopPropagation()
                        dragRef.current = { type: 'resize', artId: art.id, artIndex: rowIdx, startX: e.clientX, startY: e.clientY, startDurata: durata }
                      }}
                    >
                      <div className="w-1 h-4 bg-gray-400 rounded group-hover:bg-primary-500 transition-colors" />
                    </div>
                  )}
                  {reorderPreview?.to === rowIdx && reorderPreview.from !== rowIdx && (
                    <div className="absolute inset-0 border-2 border-primary-400 rounded pointer-events-none" />
                  )}
                </div>
              </div>
            )
          })}

          {/* Legenda */}
          <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-400">
            {dataInizio && <span>Da: {new Date(dataInizio).toLocaleDateString('it-IT')}</span>}
            <span className="text-gray-300 italic">durata = somma fasi · override con drag bordo destro</span>
            {canDrag && <span className="ml-auto italic">← trascina etichetta per riordinare · bordo destro per ridimensionare →</span>}
          </div>
        </div>
      </div>
    </div>
  )
}

// ========== ARTICOLI SECTION ==========

function ArticoliSection({
  commessa, personale,
  expandedArticoli, toggleExpandArticolo,
  showArticoloForm, editingArticolo, articoloForm, setArticoloForm,
  onCreateArticolo, onUpdateArticolo, onDeleteArticolo, onStartEditArticolo, onCancelArticoloForm, onShowNewArticoloForm,
  addingAssegnazioneArticoloId, setAddingAssegnazioneArticoloId, assegnazioneForm, setAssegnazioneForm,
  onCreateAssegnazione, onDeleteAssegnazione,
  addingFaseArticoloId, setAddingFaseArticoloId, faseForm, setFaseForm, onCreateFasePerArticolo,
  onToggleFaseCompletata, onDeleteFase, onStartEditFase,
  setFaseAllegati,
}: any) {
  const articoli: ArticoloCommessa[] = commessa.articoli || []

  return (
    <div className="bg-white rounded-xl shadow-sm mb-6">
      <div className="flex items-center justify-between p-5 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Box className="w-5 h-5 text-gray-400" />
          Articoli Commessa
          <span className="text-sm font-normal text-gray-400">({articoli.length})</span>
        </h2>
        {commessa.stato !== 'completata' && (
          <button
            onClick={onShowNewArticoloForm}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
          >
            <Plus className="w-4 h-4" />
            Nuovo Articolo
          </button>
        )}
      </div>

      {/* Articolo form (create/edit) */}
      {showArticoloForm && (
        <div className="p-5 border-b border-dashed border-gray-300 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            {editingArticolo ? 'Modifica Articolo' : 'Nuovo Articolo'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Titolo *</label>
              <input
                type="text"
                value={articoloForm.titolo}
                onChange={e => setArticoloForm((p: any) => ({ ...p, titolo: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Descrizione articolo..."
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Descrizione</label>
              <input
                type="text"
                value={articoloForm.descrizione}
                onChange={e => setArticoloForm((p: any) => ({ ...p, descrizione: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Dettagli opzionali..."
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Note Critiche</label>
              <input
                type="text"
                value={articoloForm.note_critiche}
                onChange={e => setArticoloForm((p: any) => ({ ...p, note_critiche: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                placeholder="Note importanti, vincoli, attenzioni..."
              />
            </div>
            <div className="col-span-2 flex gap-2 justify-end">
              <button onClick={onCancelArticoloForm} className="px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm">Annulla</button>
              <button
                onClick={editingArticolo ? onUpdateArticolo : onCreateArticolo}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
              >
                {editingArticolo ? 'Salva' : 'Aggiungi'}
              </button>
            </div>
          </div>
        </div>
      )}

      {articoli.length === 0 ? (
        <div className="p-8 text-center text-gray-400">
          <Box className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>Nessun articolo — crea la commessa da un'offerta per generarli automaticamente dalle voci</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {articoli.map((art, idx) => {
            const isExpanded = expandedArticoli.has(art.id)
            const fascCompletate = (art.fasi ?? []).filter((f: any) => f.completata).length

            return (
              <div key={art.id}>
                {/* Articolo Header */}
                <div
                  className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => toggleExpandArticolo(art.id)}
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}

                  <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs flex items-center justify-center font-medium flex-shrink-0">
                    {idx + 1}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{art.titolo}</span>
                      {art.note_critiche && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Nota critica
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">
                      {(art.fasi ?? []).length} fasi · {fascCompletate}/{(art.fasi ?? []).length} completate · {(art.assegnazioni ?? []).length} assegnazioni
                    </p>
                  </div>

                  {/* Progress */}
                  <div className="w-24 flex-shrink-0">
                    <div className="bg-gray-200 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full bg-primary-500"
                        style={{ width: `${(art.fasi ?? []).length > 0 ? (fascCompletate / (art.fasi ?? []).length) * 100 : 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 text-center">
                      {(art.fasi ?? []).length > 0 ? Math.round((fascCompletate / (art.fasi ?? []).length) * 100) : 0}%
                    </p>
                  </div>

                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <button onClick={() => onStartEditArticolo(art)} className="p-1.5 text-gray-400 hover:text-primary-600 rounded transition-colors">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => onDeleteArticolo(art.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Articolo Expanded */}
                {isExpanded && (
                  <div className="bg-gray-50 border-t border-gray-100 px-5 py-4">
                    {/* Note critiche */}
                    {art.note_critiche && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-orange-800">{art.note_critiche}</p>
                      </div>
                    )}
                    {art.descrizione && (
                      <p className="text-sm text-gray-500 mb-4">{art.descrizione}</p>
                    )}

                    {/* Fasi dell'articolo */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Fasi</h4>
                        {commessa.stato !== 'completata' && (
                          <button
                            onClick={() => { setAddingFaseArticoloId(art.id); setFaseForm((p: any) => ({ ...p, titolo: '', durata_prevista: 1, tipo: 'sequenziale' })) }}
                            className="text-xs text-primary-600 hover:text-primary-800 flex items-center gap-1"
                          >
                            <Plus className="w-3.5 h-3.5" /> Aggiungi Fase
                          </button>
                        )}
                      </div>

                      {/* Inline fase form per questo articolo */}
                      {addingFaseArticoloId === art.id && (
                        <div className="border border-dashed border-primary-300 rounded-lg p-3 mb-3 bg-white">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                            <div className="col-span-2">
                              <select
                                value={faseForm.titolo}
                                onChange={e => setFaseForm((p: any) => ({ ...p, titolo: e.target.value }))}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white"
                              >
                                <option value="">Seleziona titolo...</option>
                                {FASE_TITOLI.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </div>
                            <div>
                              <input
                                type="number"
                                min="1"
                                placeholder="Giorni"
                                value={faseForm.durata_prevista}
                                onChange={e => setFaseForm((p: any) => ({ ...p, durata_prevista: parseInt(e.target.value) || 1 }))}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                              />
                            </div>
                            <div>
                              <select
                                value={faseForm.tipo}
                                onChange={e => setFaseForm((p: any) => ({ ...p, tipo: e.target.value }))}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white"
                              >
                                <option value="sequenziale">Seq.</option>
                                <option value="parallelo">Par.</option>
                              </select>
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-400 mb-0.5">Scadenza</label>
                              <input
                                type="date"
                                value={faseForm.data_scadenza || ''}
                                onChange={e => setFaseForm((p: any) => ({ ...p, data_scadenza: e.target.value || null }))}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-400 mb-0.5">Note critiche</label>
                              <input
                                type="text"
                                placeholder="Vincoli, attenzioni..."
                                value={faseForm.note_critiche || ''}
                                onChange={e => setFaseForm((p: any) => ({ ...p, note_critiche: e.target.value || null }))}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setAddingFaseArticoloId(null)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">Annulla</button>
                            <button onClick={() => onCreateFasePerArticolo(art.id)} className="text-xs bg-primary-600 text-white px-3 py-1 rounded hover:bg-primary-700">Aggiungi</button>
                          </div>
                        </div>
                      )}

                      {(art.fasi ?? []).length === 0 ? (
                        <p className="text-xs text-gray-400 py-2">Nessuna fase</p>
                      ) : (
                        <div className="space-y-1">
                          {(art.fasi ?? []).map((fase: any) => (
                            <div key={fase.id} className="flex items-center gap-2 p-2 bg-white rounded border border-gray-100">
                              <button
                                onClick={() => onToggleFaseCompletata(fase)}
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                  fase.completata ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'
                                }`}
                              >
                                {fase.completata && <Check className="w-2.5 h-2.5" />}
                              </button>
                              <span className={`text-sm flex-1 ${fase.completata ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                {fase.titolo}
                              </span>
                              <span className="text-xs text-gray-400">{fase.giorni_effettivi}/{fase.durata_prevista}gg</span>
                              {fase.data_scadenza && (
                                <span className={`text-xs px-1 rounded flex items-center gap-0.5 ${
                                  new Date(fase.data_scadenza) < new Date() && !fase.completata
                                    ? 'bg-red-100 text-red-600' : 'bg-blue-50 text-blue-600'
                                }`}>
                                  <Calendar className="w-2.5 h-2.5" />
                                  {new Date(fase.data_scadenza).toLocaleDateString('it-IT', { day:'2-digit', month:'2-digit' })}
                                </span>
                              )}
                              {fase.in_ritardo && <AlertTriangle className="w-3.5 h-3.5 text-red-400" />}
                              <span className={`text-xs px-1 rounded ${fase.tipo === 'parallelo' ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-500'}`}>
                                {fase.tipo === 'parallelo' ? 'P' : 'S'}
                              </span>
                              <button onClick={() => setFaseAllegati({ id: fase.id, titolo: fase.titolo })} className="p-1 text-gray-300 hover:text-amber-500">
                                <Paperclip className="w-3 h-3" />
                              </button>
                              <button onClick={() => onStartEditFase(fase)} className="p-1 text-gray-300 hover:text-primary-500">
                                <Pencil className="w-3 h-3" />
                              </button>
                              <button onClick={() => onDeleteFase(fase.id, fase.titolo)} className="p-1 text-gray-300 hover:text-red-500">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Assegnazioni dirette */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Assegnazioni Dirette</h4>
                        {commessa.stato !== 'completata' && (
                          <button
                            onClick={() => { setAddingAssegnazioneArticoloId(art.id); setAssegnazioneForm({ personale_id: '', giorni: 1, note: '' }) }}
                            className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1"
                          >
                            <UserPlus className="w-3.5 h-3.5" /> Assegna
                          </button>
                        )}
                      </div>

                      {addingAssegnazioneArticoloId === art.id && (
                        <div className="border border-dashed border-teal-300 rounded-lg p-3 mb-3 bg-white">
                          <div className="grid grid-cols-3 gap-2 mb-2">
                            <div className="col-span-2">
                              <select
                                value={assegnazioneForm.personale_id}
                                onChange={e => setAssegnazioneForm((p: any) => ({ ...p, personale_id: e.target.value }))}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white"
                              >
                                <option value="">Seleziona persona...</option>
                                {personale.map((p: any) => (
                                  <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <input
                                type="number"
                                min="1"
                                placeholder="Giorni"
                                value={assegnazioneForm.giorni}
                                onChange={e => setAssegnazioneForm((p: any) => ({ ...p, giorni: parseInt(e.target.value) || 1 }))}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setAddingAssegnazioneArticoloId(null)} className="text-xs text-gray-500 px-2 py-1">Annulla</button>
                            <button onClick={() => onCreateAssegnazione(art.id)} className="text-xs bg-teal-600 text-white px-3 py-1 rounded hover:bg-teal-700">Assegna</button>
                          </div>
                        </div>
                      )}

                      {art.assegnazioni.length === 0 ? (
                        <p className="text-xs text-gray-400 py-1">Nessuna assegnazione diretta</p>
                      ) : (
                        <div className="space-y-1">
                          {art.assegnazioni.map((a: any) => (
                            <div key={a.id} className="flex items-center gap-2 p-2 bg-white rounded border border-gray-100 text-sm">
                              <Users className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                              <span className="flex-1">{a.personale_nome}</span>
                              <span className="text-xs text-gray-400">{a.giorni} giorni</span>
                              {a.note && <span className="text-xs text-gray-400">{a.note}</span>}
                              <button onClick={() => onDeleteAssegnazione(a.id)} className="p-1 text-gray-300 hover:text-red-500">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Assegnazioni senza articolo */}
      {(commessa.assegnazioni_senza_articolo || []).length > 0 && (
        <div className="border-t border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-600 mb-2">Assegnazioni generiche</h3>
          <div className="space-y-1">
            {(commessa.assegnazioni_senza_articolo || []).map((a: any) => (
              <div key={a.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm">
                <Users className="w-3.5 h-3.5 text-gray-400" />
                <span className="flex-1">{a.personale_nome}</span>
                <span className="text-xs text-gray-400">{a.giorni} giorni</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface CostiFase {
  fase_id: string | null
  fase_label: string
  giorni_effettivi: number
  costo_personale: number
  costo_mezzi: number
  costo_totale: number
}

interface CostiCommessa {
  commessa_id: string
  totale_costo: number
  fasi: CostiFase[]
}

const statoBadge: Record<StatoCommessa, { label: string; bg: string; text: string }> = {
  pianificazione: { label: 'Pianificazione', bg: 'bg-gray-100', text: 'text-gray-700' },
  in_revisione: { label: 'In Revisione', bg: 'bg-purple-100', text: 'text-purple-700' },
  approvata: { label: 'Approvata', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  in_esecuzione: { label: 'In Esecuzione', bg: 'bg-blue-100', text: 'text-blue-700' },
  completata: { label: 'Completata', bg: 'bg-green-100', text: 'text-green-700' },
  sospesa: { label: 'Sospesa', bg: 'bg-amber-100', text: 'text-amber-700' },
  rifiutata: { label: 'Rifiutata', bg: 'bg-red-100', text: 'text-red-700' },
}

export default function CommessaDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [commessa, setCommessa] = useState<CommessaDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [personale, setPersonale] = useState<PersonaleMinimal[]>([])
  const [strumenti, setStrumenti] = useState<StrumentoMinimal[]>([])

  const [costiCalendario, setCostiCalendario] = useState<CostiCommessa | null>(null)
  const [showTransfer, setShowTransfer] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null)
  const [showAllegati, setShowAllegati] = useState(false)
  const [faseAllegati, setFaseAllegati] = useState<{ id: string; titolo: string } | null>(null)
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null)

  // Fase form
  const [showFaseForm, setShowFaseForm] = useState(false)
  const [editingFase, setEditingFase] = useState<FaseDetail | null>(null)
  const [faseForm, setFaseForm] = useState<FaseForm>({
    titolo: '', durata_prevista: 1, responsabile_id: '', tipo: 'sequenziale',
    partecipanti_ids: [], strumenti_ids: []
  })

  // Giorno form
  const [addingGiornoFaseId, setAddingGiornoFaseId] = useState<string | null>(null)
  const [giornoForm, setGiornoForm] = useState<GiornoFaseForm>({
    data: new Date().toISOString().split('T')[0],
    partecipanti_ids: [],
    strumenti_ids: [],
    note: ''
  })

  // Expanded fasi
  const [expandedFasi, setExpandedFasi] = useState<Set<string>>(new Set())

  // Vista mode
  const [viewMode, setViewMode] = useState<'articoli' | 'fasi'>('articoli')

  // Expanded articoli
  const [expandedArticoli, setExpandedArticoli] = useState<Set<string>>(new Set())

  // Articolo form
  const [showArticoloForm, setShowArticoloForm] = useState(false)
  const [editingArticolo, setEditingArticolo] = useState<ArticoloCommessa | null>(null)
  const [articoloForm, setArticoloForm] = useState({ titolo: '', descrizione: '', note_critiche: '' })

  // Assegnazione form
  const [addingAssegnazioneArticoloId, setAddingAssegnazioneArticoloId] = useState<string | null>(null)
  const [assegnazioneForm, setAssegnazioneForm] = useState({ personale_id: '', giorni: 1, note: '' })

  // Fase su articolo form
  const [addingFaseArticoloId, setAddingFaseArticoloId] = useState<string | null>(null)

  // Vista Fasi — titoli espansi + form aggiungi articolo
  const [expandedTitoli, setExpandedTitoli] = useState<Set<string>>(new Set())
  const [addingFaseToTitolo, setAddingFaseToTitolo] = useState<string | null>(null)
  const [addFaseToTitoloArtId, setAddFaseToTitoloArtId] = useState('')
  const [addFaseToTitoloDurata, setAddFaseToTitoloDurata] = useState(1)
  const [addFaseToTitoloTipo, setAddFaseToTitoloTipo] = useState<'sequenziale' | 'parallelo'>('sequenziale')

  useEffect(() => {
    loadData()
  }, [id])

  const loadData = async () => {
    try {
      setLoading(true)
      const [commessaRes, personaleRes, strumentiRes] = await Promise.all([
        api.get(`/v1/commesse/${id}`),
        api.get('/v1/personale'),
        api.get('/v1/strumenti')
      ])
      setCommessa(commessaRes.data)
      setPersonale(personaleRes.data.map((p: any) => ({
        id: p.id, nome: p.nome, cognome: p.cognome
      })))
      setStrumenti(strumentiRes.data.map((s: any) => ({
        id: s.id, descrizione: s.descrizione
      })))
      // Carica costi dal calendario
      try {
        const costiRes = await api.get(`/v1/calendario/costi-commessa/${id}`)
        setCostiCalendario(costiRes.data)
      } catch { /* calendario non ancora configurato */ }
    } catch {
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateStato = async (newStato: StatoCommessa) => {
    if (!commessa) return
    try {
      let res
      if (newStato === 'in_esecuzione' && commessa.stato === 'pianificazione') {
        // Usa endpoint dedicato /avvia
        res = await api.post(`/v1/commesse/${commessa.id}/avvia`)
        setCommessa(res.data)
        if (res.data.avviso_ritardo) {
          setToast({ message: res.data.avviso_ritardo, type: 'warning' })
        } else {
          setToast({ message: 'Commessa avviata', type: 'success' })
        }
      } else {
        const updates: any = { stato: newStato }
        if (newStato === 'completata') {
          updates.data_completamento = new Date().toISOString().split('T')[0]
        }
        res = await api.put(`/v1/commesse/${commessa.id}`, updates)
        setCommessa(res.data)
        setToast({ message: 'Stato aggiornato', type: 'success' })
      }
    } catch {
      setToast({ message: 'Errore aggiornamento stato', type: 'error' })
    }
    setConfirmAction(null)
  }

  // ========== FASI ==========

  const handleCreateFase = async () => {
    if (!commessa || !faseForm.titolo) return
    try {
      const res = await api.post(`/v1/commesse/${commessa.id}/fasi`, {
        titolo: faseForm.titolo,
        durata_prevista: faseForm.durata_prevista,
        responsabile_id: faseForm.responsabile_id || commessa.responsabile_id,
        tipo: faseForm.tipo,
      })
      setCommessa(res.data)
      setShowFaseForm(false)
      resetFaseForm()
      setToast({ message: 'Fase creata', type: 'success' })
    } catch {
      setToast({ message: 'Errore creazione fase', type: 'error' })
    }
  }

  const handleUpdateFase = async () => {
    if (!commessa || !editingFase || !faseForm.titolo) return
    try {
      const res = await api.put(`/v1/commesse/${commessa.id}/fasi/${editingFase.id}`, {
        titolo: faseForm.titolo,
        durata_prevista: faseForm.durata_prevista,
        responsabile_id: faseForm.responsabile_id,
        tipo: faseForm.tipo,
      })
      setCommessa(res.data)
      setEditingFase(null)
      setShowFaseForm(false)
      resetFaseForm()
      setToast({ message: 'Fase aggiornata', type: 'success' })
    } catch {
      setToast({ message: 'Errore aggiornamento fase', type: 'error' })
    }
  }

  const handleToggleFaseCompletata = async (fase: FaseDetail) => {
    if (!commessa) return
    try {
      const res = await api.put(`/v1/commesse/${commessa.id}/fasi/${fase.id}`, {
        completata: !fase.completata,
      })
      setCommessa(res.data)
    } catch {
      setToast({ message: 'Errore aggiornamento fase', type: 'error' })
    }
  }

  const handleDeleteFase = async (faseId: string) => {
    if (!commessa) return
    try {
      await api.delete(`/v1/commesse/${commessa.id}/fasi/${faseId}`)
      setCommessa(prev => prev ? {
        ...prev,
        fasi: prev.fasi.filter(f => f.id !== faseId),
        fasi_totali: prev.fasi_totali - 1,
      } : null)
      setToast({ message: 'Fase eliminata', type: 'success' })
    } catch {
      setToast({ message: 'Errore eliminazione fase', type: 'error' })
    }
    setConfirmAction(null)
  }

  const resetFaseForm = () => {
    setFaseForm({
      titolo: '', durata_prevista: 1,
      responsabile_id: commessa?.responsabile_id || '',
      tipo: 'sequenziale',
      partecipanti_ids: [], strumenti_ids: []
    })
  }

  const startEditFase = (fase: FaseDetail) => {
    setEditingFase(fase)
    setFaseForm({
      titolo: fase.titolo,
      durata_prevista: fase.durata_prevista,
      responsabile_id: fase.responsabile_id,
      tipo: fase.tipo,
      partecipanti_ids: fase.partecipanti_ids || [],
      strumenti_ids: fase.strumenti_ids || [],
    })
    setShowFaseForm(true)
  }

  // ========== GIORNI ==========

  const handleCreateGiorno = async (faseId: string) => {
    if (!commessa) return
    try {
      const res = await api.post(`/v1/commesse/${commessa.id}/fasi/${faseId}/giorni`, giornoForm)
      setCommessa(res.data)
      setAddingGiornoFaseId(null)
      setGiornoForm({
        data: new Date().toISOString().split('T')[0],
        partecipanti_ids: [],
        strumenti_ids: [],
        note: ''
      })
      setToast({ message: 'Giorno aggiunto', type: 'success' })
    } catch {
      setToast({ message: 'Errore aggiunta giorno', type: 'error' })
    }
  }

  const handleDeleteGiorno = async (faseId: string, giornoId: string) => {
    if (!commessa) return
    try {
      await api.delete(`/v1/commesse/${commessa.id}/fasi/${faseId}/giorni/${giornoId}`)
      // Ricarica
      const res = await api.get(`/v1/commesse/${commessa.id}`)
      setCommessa(res.data)
      setToast({ message: 'Giorno eliminato', type: 'success' })
    } catch {
      setToast({ message: 'Errore eliminazione giorno', type: 'error' })
    }
  }

  const toggleExpandFase = (faseId: string) => {
    setExpandedFasi(prev => {
      const next = new Set(prev)
      if (next.has(faseId)) next.delete(faseId)
      else next.add(faseId)
      return next
    })
  }

  const togglePartecipante = (personaleId: string) => {
    setGiornoForm(prev => ({
      ...prev,
      partecipanti_ids: prev.partecipanti_ids.includes(personaleId)
        ? prev.partecipanti_ids.filter(id => id !== personaleId)
        : [...prev.partecipanti_ids, personaleId]
    }))
  }

  const toggleStrumento = (strumentoId: string) => {
    setGiornoForm(prev => ({
      ...prev,
      strumenti_ids: prev.strumenti_ids.includes(strumentoId)
        ? prev.strumenti_ids.filter(id => id !== strumentoId)
        : [...prev.strumenti_ids, strumentoId]
    }))
  }

  const getPersonaleNome = (id: string) => {
    const p = personale.find(p => p.id === id)
    return p ? `${p.cognome} ${p.nome}` : id.substring(0, 8)
  }

  const getStrumentoNome = (id: string) => {
    const s = strumenti.find(s => s.id === id)
    return s ? s.descrizione : id.substring(0, 8)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    })
  }

  const toggleExpandArticolo = (artId: string) => {
    setExpandedArticoli(prev => {
      const next = new Set(prev)
      if (next.has(artId)) next.delete(artId)
      else next.add(artId)
      return next
    })
  }

  // ========== ARTICOLI ==========

  const handleCreateArticolo = async () => {
    if (!commessa || !articoloForm.titolo) return
    try {
      const res = await api.post(`/v1/commesse/${commessa.id}/articoli`, {
        titolo: articoloForm.titolo,
        descrizione: articoloForm.descrizione || null,
        note_critiche: articoloForm.note_critiche || null,
      })
      setCommessa(res.data)
      setShowArticoloForm(false)
      setArticoloForm({ titolo: '', descrizione: '', note_critiche: '' })
      setToast({ message: 'Articolo creato', type: 'success' })
    } catch {
      setToast({ message: 'Errore creazione articolo', type: 'error' })
    }
  }

  const handleUpdateArticolo = async () => {
    if (!commessa || !editingArticolo) return
    try {
      const res = await api.put(`/v1/commesse/${commessa.id}/articoli/${editingArticolo.id}`, {
        titolo: articoloForm.titolo,
        descrizione: articoloForm.descrizione || null,
        note_critiche: articoloForm.note_critiche || null,
      })
      setCommessa(res.data)
      setEditingArticolo(null)
      setShowArticoloForm(false)
      setToast({ message: 'Articolo aggiornato', type: 'success' })
    } catch {
      setToast({ message: 'Errore aggiornamento articolo', type: 'error' })
    }
  }

  const handleDeleteArticolo = async (articoloId: string) => {
    if (!commessa) return
    try {
      await api.delete(`/v1/commesse/${commessa.id}/articoli/${articoloId}`)
      const res = await api.get(`/v1/commesse/${commessa.id}`)
      setCommessa(res.data)
      setToast({ message: 'Articolo eliminato', type: 'success' })
    } catch {
      setToast({ message: 'Errore eliminazione articolo', type: 'error' })
    }
    setConfirmAction(null)
  }

  const startEditArticolo = (art: ArticoloCommessa) => {
    setEditingArticolo(art)
    setArticoloForm({ titolo: art.titolo, descrizione: art.descrizione || '', note_critiche: art.note_critiche || '' })
    setShowArticoloForm(true)
  }

  // ========== ASSEGNAZIONI ==========

  const handleCreateAssegnazione = async (articoloId: string | null) => {
    if (!commessa || !assegnazioneForm.personale_id) return
    try {
      const res = await api.post(`/v1/commesse/${commessa.id}/assegnazioni`, {
        articolo_id: articoloId,
        personale_id: assegnazioneForm.personale_id,
        giorni: assegnazioneForm.giorni,
        note: assegnazioneForm.note || null,
      })
      setCommessa(res.data)
      setAddingAssegnazioneArticoloId(null)
      setAssegnazioneForm({ personale_id: '', giorni: 1, note: '' })
      setToast({ message: 'Assegnazione aggiunta', type: 'success' })
    } catch {
      setToast({ message: 'Errore assegnazione', type: 'error' })
    }
  }

  const handleDeleteAssegnazione = async (assegnazioneId: string) => {
    if (!commessa) return
    try {
      await api.delete(`/v1/commesse/${commessa.id}/assegnazioni/${assegnazioneId}`)
      const res = await api.get(`/v1/commesse/${commessa.id}`)
      setCommessa(res.data)
    } catch {
      setToast({ message: 'Errore eliminazione assegnazione', type: 'error' })
    }
  }

  // ========== FASE A TITOLO (Vista Fasi grouped) ==========

  const toggleExpandTitolo = (titolo: string) => {
    setExpandedTitoli(prev => {
      const next = new Set(prev)
      if (next.has(titolo)) next.delete(titolo)
      else next.add(titolo)
      return next
    })
  }

  const handleAddFaseToTitolo = async (titolo: string) => {
    if (!commessa || !addFaseToTitoloArtId) return
    try {
      const res = await api.post(`/v1/commesse/${commessa.id}/fasi`, {
        titolo,
        durata_prevista: addFaseToTitoloDurata,
        responsabile_id: commessa.responsabile_id,
        tipo: addFaseToTitoloTipo,
        articolo_id: addFaseToTitoloArtId,
      })
      setCommessa(res.data)
      setAddingFaseToTitolo(null)
      setAddFaseToTitoloArtId('')
      setAddFaseToTitoloDurata(1)
      setAddFaseToTitoloTipo('sequenziale')
      setToast({ message: 'Fase creata', type: 'success' })
    } catch {
      setToast({ message: 'Errore creazione fase', type: 'error' })
    }
  }

  // ========== GANTT ARTICOLI CALLBACKS ==========

  const handleArticoloDurataChange = async (articoloId: string, newDurata: number) => {
    if (!commessa) return
    try {
      const res = await api.put(`/v1/commesse/${commessa.id}/articoli/${articoloId}`, { durata_gantt: newDurata })
      setCommessa(res.data)
    } catch {
      setToast({ message: 'Errore aggiornamento durata articolo', type: 'error' })
    }
  }

  const handleArticoliReorder = async (orderedIds: string[]) => {
    if (!commessa) return
    try {
      const res = await api.put(`/v1/commesse/${commessa.id}/articoli/riordina`, { articolo_ids: orderedIds })
      setCommessa(res.data)
    } catch {
      setToast({ message: 'Errore riordinamento articoli', type: 'error' })
    }
  }

  // ========== GANTT CALLBACKS ==========

  const handleDurataChange = async (faseId: string, newDurata: number) => {
    if (!commessa) return
    try {
      const res = await api.put(`/v1/commesse/${commessa.id}/fasi/${faseId}`, { durata_prevista: newDurata })
      setCommessa(res.data)
    } catch {
      setToast({ message: 'Errore aggiornamento durata', type: 'error' })
    }
  }

  const handleReorder = async (orderedFaseIds: string[]) => {
    if (!commessa) return
    try {
      const res = await api.put(`/v1/commesse/${commessa.id}/fasi/riordina`, { fase_ids: orderedFaseIds })
      setCommessa(res.data)
    } catch {
      setToast({ message: 'Errore riordinamento fasi', type: 'error' })
    }
  }

  // ========== FASE SU ARTICOLO ==========

  const handleCreateFasePerArticolo = async (articoloId: string) => {
    if (!commessa || !faseForm.titolo) return
    try {
      const res = await api.post(`/v1/commesse/${commessa.id}/fasi`, {
        titolo: faseForm.titolo,
        durata_prevista: faseForm.durata_prevista,
        responsabile_id: faseForm.responsabile_id || commessa.responsabile_id,
        tipo: faseForm.tipo,
        articolo_id: articoloId,
        data_scadenza: faseForm.data_scadenza || null,
        note_critiche: faseForm.note_critiche || null,
      })
      setCommessa(res.data)
      setAddingFaseArticoloId(null)
      resetFaseForm()
      setToast({ message: 'Fase creata', type: 'success' })
    } catch {
      setToast({ message: 'Errore creazione fase', type: 'error' })
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Caricamento...</div>
      </div>
    )
  }

  if (!commessa) {
    return <div className="text-center text-gray-500 py-8">Commessa non trovata</div>
  }

  const badge = statoBadge[commessa.stato]
  const today = new Date().toISOString().split('T')[0]
  const isOverdue = commessa.data_prevista_consegna < today && commessa.stato !== 'completata'

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <ConfirmModal
        isOpen={!!confirmAction}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmText="Conferma"
        onConfirm={() => confirmAction?.onConfirm()}
        onCancel={() => setConfirmAction(null)}
      />

      <AllegatiModal
        isOpen={showAllegati}
        onClose={() => setShowAllegati(false)}
        entityType="commessa"
        entityId={commessa.id}
      />

      {faseAllegati && (
        <FaseAllegatiModal
          faseId={faseAllegati.id}
          faseTitolo={faseAllegati.titolo}
          onClose={() => setFaseAllegati(null)}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/app/commesse')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
              {commessa.numero_commessa}
            </span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
              {badge.label}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">{commessa.titolo}</h1>
          <p className="text-gray-500">{commessa.cliente_nome} - Resp: {commessa.responsabile_nome}</p>
        </div>

        {/* Azioni */}
        <div className="flex items-center gap-2">
          <OwnershipBadge
            ownerId={commessa.owner_id}
            ownerName={commessa.owner_name}
            onTransfer={() => setShowTransfer(true)}
          />
          <TransferOwnershipModal
            isOpen={showTransfer}
            onClose={() => setShowTransfer(false)}
            entityType="commessa"
            entityId={commessa.id}
            currentOwnerId={commessa.owner_id}
            onTransferred={() => {
              setToast({ message: 'Proprietà trasferita', type: 'success' })
              loadData()
            }}
          />
          {commessa.stato === 'pianificazione' && (
            <button
              onClick={() => setConfirmAction({
                title: 'Avvia Commessa',
                message: 'Vuoi avviare l\'esecuzione della commessa?',
                onConfirm: () => handleUpdateStato('in_esecuzione')
              })}
              className="flex items-center gap-2 px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50"
            >
              <Play className="w-4 h-4" />
              Avvia
            </button>
          )}
          {commessa.stato === 'in_esecuzione' && (
            <>
              <button
                onClick={() => setConfirmAction({
                  title: 'Completa Commessa',
                  message: 'Vuoi segnare la commessa come completata?',
                  onConfirm: () => handleUpdateStato('completata')
                })}
                className="flex items-center gap-2 px-4 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50"
              >
                <CheckCircle className="w-4 h-4" />
                Completa
              </button>
              <button
                onClick={() => setConfirmAction({
                  title: 'Sospendi Commessa',
                  message: 'Vuoi sospendere la commessa?',
                  onConfirm: () => handleUpdateStato('sospesa')
                })}
                className="flex items-center gap-2 px-3 py-2 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50"
              >
                <Pause className="w-4 h-4" />
              </button>
            </>
          )}
          {commessa.stato === 'sospesa' && (
            <button
              onClick={() => handleUpdateStato('in_esecuzione')}
              className="flex items-center gap-2 px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50"
            >
              <Play className="w-4 h-4" />
              Riprendi
            </button>
          )}
          <button
            onClick={() => setShowAllegati(true)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate(`/app/offerte/${commessa.offerta_id}`)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            title="Vai all'offerta"
          >
            <FileText className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Ore Offerta</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{commessa.ore_previste_offerta || commessa.giorni_previsti}</p>
        </div>
        <div className={`bg-white rounded-xl shadow-sm p-4 ${
          commessa.ore_gantt_totali > (commessa.ore_previste_offerta || commessa.giorni_previsti) && (commessa.ore_previste_offerta || commessa.giorni_previsti) > 0
            ? 'ring-2 ring-red-400' : ''
        }`}>
          <p className="text-xs text-gray-500 uppercase font-medium">Ore Gantt</p>
          <p className={`text-2xl font-bold mt-1 ${
            commessa.ore_gantt_totali > (commessa.ore_previste_offerta || commessa.giorni_previsti) && (commessa.ore_previste_offerta || commessa.giorni_previsti) > 0
              ? 'text-red-600' : 'text-gray-900'
          }`}>
            {commessa.ore_gantt_totali}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Giorni Svolti</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{commessa.giorni_svolti}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Fasi</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {commessa.fasi_completate}/{commessa.fasi_totali}
          </p>
        </div>
        <div className={`bg-white rounded-xl shadow-sm p-4 ${isOverdue ? 'ring-2 ring-red-400' : ''}`}>
          <p className="text-xs text-gray-500 uppercase font-medium">Consegna</p>
          <p className={`text-lg font-bold mt-1 ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
            {formatDate(commessa.data_prevista_consegna)}
          </p>
          {isOverdue && <p className="text-xs text-red-500">In ritardo</p>}
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Costo Cal.</p>
          <p className="text-xl font-bold text-gray-900 mt-1">
            {costiCalendario && costiCalendario.totale_costo > 0
              ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(costiCalendario.totale_costo)
              : '—'
            }
          </p>
        </div>
      </div>

      {/* Avviso ore */}
      {commessa.avviso_ore && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Attenzione Ore</p>
            <p className="text-sm text-amber-700">{commessa.avviso_ore}</p>
          </div>
        </div>
      )}

      {/* Vista Toggle */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setViewMode('articoli')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
            viewMode === 'articoli' ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <LayoutGrid className="w-4 h-4" />
          Vista Articoli
        </button>
        <button
          onClick={() => setViewMode('fasi')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
            viewMode === 'fasi' ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <List className="w-4 h-4" />
          Vista Fasi
        </button>
      </div>

      {/* Gantt Chart */}
      <div className="mb-6">
        {viewMode === 'fasi' ? (
          <GanttChart
            fasi={commessa.fasi}
            articoli={(commessa.articoli || []).map(a => ({ id: a.id, ordine: a.ordine }))}
            dataInizio={commessa.data_inizio}
            dataConsegna={commessa.data_prevista_consegna}
            stato={commessa.stato}
            onDurataChange={handleDurataChange}
            onReorder={handleReorder}
          />
        ) : (
          /* Gantt per articolo: barre sequenziali */
          <GanttArticoli
            articoli={commessa.articoli || []}
            dataInizio={commessa.data_inizio}
            onReorder={handleArticoliReorder}
            onDurataChange={handleArticoloDurataChange}
          />
        )}
      </div>

      {/* =========== ARTICOLI SECTION =========== */}
      {viewMode === 'articoli' && (
        <ArticoliSection
          commessa={commessa}
          personale={personale}
          expandedArticoli={expandedArticoli}
          toggleExpandArticolo={toggleExpandArticolo}
          showArticoloForm={showArticoloForm}
          editingArticolo={editingArticolo}
          articoloForm={articoloForm}
          setArticoloForm={setArticoloForm}
          onCreateArticolo={handleCreateArticolo}
          onUpdateArticolo={handleUpdateArticolo}
          onDeleteArticolo={(id: string) => setConfirmAction({
            title: 'Elimina Articolo',
            message: 'Eliminare l\'articolo e tutte le sue assegnazioni? Le fasi vengono scollegate.',
            onConfirm: () => handleDeleteArticolo(id)
          })}
          onStartEditArticolo={startEditArticolo}
          onCancelArticoloForm={() => { setShowArticoloForm(false); setEditingArticolo(null) }}
          onShowNewArticoloForm={() => {
            setEditingArticolo(null)
            setArticoloForm({ titolo: '', descrizione: '', note_critiche: '' })
            setShowArticoloForm(true)
          }}
          addingAssegnazioneArticoloId={addingAssegnazioneArticoloId}
          setAddingAssegnazioneArticoloId={setAddingAssegnazioneArticoloId}
          assegnazioneForm={assegnazioneForm}
          setAssegnazioneForm={setAssegnazioneForm}
          onCreateAssegnazione={handleCreateAssegnazione}
          onDeleteAssegnazione={handleDeleteAssegnazione}
          addingFaseArticoloId={addingFaseArticoloId}
          setAddingFaseArticoloId={setAddingFaseArticoloId}
          faseForm={faseForm}
          setFaseForm={setFaseForm}
          onCreateFasePerArticolo={handleCreateFasePerArticolo}
          onToggleFaseCompletata={handleToggleFaseCompletata}
          onDeleteFase={(faseId: string, faseTitolo: string) => setConfirmAction({
            title: 'Elimina Fase',
            message: `Eliminare la fase "${faseTitolo}" e tutti i suoi giorni?`,
            onConfirm: () => handleDeleteFase(faseId)
          })}
          onStartEditFase={startEditFase}
          setFaseAllegati={setFaseAllegati}
        />
      )}

      {/* Fasi Section */}
      {viewMode === 'fasi' && (
      <div className="bg-white rounded-xl shadow-sm">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <List className="w-5 h-5 text-gray-400" />
            Fasi di Lavoro
          </h2>
        </div>

        {/* Form modifica fase (solo edit) */}
        {showFaseForm && editingFase && (
          <div className="p-5 border-b border-dashed border-gray-300 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Modifica Fase</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Titolo fase *</label>
                <select
                  value={faseForm.titolo}
                  onChange={e => setFaseForm(prev => ({ ...prev, titolo: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm bg-white"
                >
                  <option value="">Seleziona titolo...</option>
                  {FASE_TITOLI.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Durata (giorni) *</label>
                <input
                  type="number"
                  min="1"
                  value={faseForm.durata_prevista}
                  onChange={e => setFaseForm(prev => ({ ...prev, durata_prevista: parseInt(e.target.value) || 1 }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Tipo</label>
                <select
                  value={faseForm.tipo}
                  onChange={e => setFaseForm(prev => ({ ...prev, tipo: e.target.value as 'sequenziale' | 'parallelo' }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm bg-white"
                >
                  <option value="sequenziale">Sequenziale</option>
                  <option value="parallelo">Parallelo</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Responsabile</label>
                <select
                  value={faseForm.responsabile_id}
                  onChange={e => setFaseForm(prev => ({ ...prev, responsabile_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm bg-white"
                >
                  <option value="">Default commessa</option>
                  {personale.map(p => (
                    <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>
                  ))}
                </select>
              </div>
              {/* Partecipanti multi-select */}
              <div className="col-span-2">
                <label className="block text-xs text-gray-500 mb-1">Personale assegnato</label>
                <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-white">
                  {personale.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setFaseForm(prev => ({
                          ...prev,
                          partecipanti_ids: prev.partecipanti_ids.includes(p.id)
                            ? prev.partecipanti_ids.filter(id => id !== p.id)
                            : [...prev.partecipanti_ids, p.id]
                        }))
                      }}
                      className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                        faseForm.partecipanti_ids.includes(p.id)
                          ? 'bg-blue-100 border-blue-300 text-blue-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'
                      }`}
                    >
                      {p.cognome} {p.nome}
                    </button>
                  ))}
                </div>
              </div>

              {/* Strumenti multi-select */}
              {strumenti.length > 0 && (
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Strumenti / Mezzi</label>
                  <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-white">
                    {strumenti.map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => {
                          setFaseForm(prev => ({
                            ...prev,
                            strumenti_ids: prev.strumenti_ids.includes(s.id)
                              ? prev.strumenti_ids.filter(id => id !== s.id)
                              : [...prev.strumenti_ids, s.id]
                          }))
                        }}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                          faseForm.strumenti_ids.includes(s.id)
                            ? 'bg-amber-100 border-amber-300 text-amber-700'
                            : 'bg-white border-gray-200 text-gray-600 hover:border-amber-300'
                        }`}
                      >
                        {s.descrizione}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="col-span-4 flex gap-2 justify-end">
                <button
                  onClick={() => { setShowFaseForm(false); setEditingFase(null) }}
                  className="px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm"
                >
                  Annulla
                </button>
                <button
                  onClick={editingFase ? handleUpdateFase : handleCreateFase}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                >
                  {editingFase ? 'Salva' : 'Aggiungi'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lista completa titoli fasi */}
        {(() => {
          // Mappa articolo_id → {num, titolo, color}
          const artMap: Record<string, { num: number; titolo: string; color: typeof ARTICOLO_COLORS[0] }> = {}
          ;(commessa.articoli || []).forEach((a, i) => {
            artMap[a.id] = { num: a.ordine + 1, titolo: a.titolo, color: ARTICOLO_COLORS[i % ARTICOLO_COLORS.length] }
          })

          // Tutti i titoli: FASE_TITOLI + eventuali custom presenti in commessa
          const customTitoli = commessa.fasi
            .map(f => f.titolo)
            .filter(t => !FASE_TITOLI.includes(t))
          const allTitoli = [...FASE_TITOLI, ...Array.from(new Set(customTitoli))]

          // Raggruppa fasi per titolo
          const grouped: Record<string, typeof commessa.fasi> = {}
          commessa.fasi.forEach(f => {
            if (!grouped[f.titolo]) grouped[f.titolo] = []
            grouped[f.titolo].push(f)
          })

          return (
          <div className="divide-y divide-gray-100">
            {allTitoli.map((titolo, titoloIdx) => {
              const fasesGroup = grouped[titolo] || []
              const completateInGroup = fasesGroup.filter(f => f.completata).length
              const isExpTitolo = expandedTitoli.has(titolo)
              const articoliConTitolo = new Set(fasesGroup.map(f => f.articolo_id).filter(Boolean))
              const articoliDisponibili = (commessa.articoli || []).filter(a => !articoliConTitolo.has(a.id))
              const isAddingHere = addingFaseToTitolo === titolo

              return (
                <div key={titolo}>
                  {/* Header titolo — collassabile */}
                  <div
                    className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => toggleExpandTitolo(titolo)}
                  >
                    {isExpTitolo ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs flex items-center justify-center font-medium">
                      {titoloIdx + 1}
                    </div>
                    <span className="font-medium text-gray-900 flex-1">{titolo}</span>
                    {fasesGroup.length > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${completateInGroup === fasesGroup.length ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {completateInGroup}/{fasesGroup.length}
                      </span>
                    )}
                    {fasesGroup.length === 0 && (
                      <span className="text-xs text-gray-300 italic flex-shrink-0">nessun articolo</span>
                    )}
                    {commessa.stato !== 'completata' && articoliDisponibili.length > 0 && (
                      <button
                        onClick={e => { e.stopPropagation(); setAddingFaseToTitolo(isAddingHere ? null : titolo); setAddFaseToTitoloArtId(''); setAddFaseToTitoloDurata(1); setAddFaseToTitoloTipo('sequenziale'); if (!isExpTitolo) toggleExpandTitolo(titolo) }}
                        className="flex-shrink-0 text-xs text-primary-600 hover:text-primary-800 flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Aggiungi articolo
                      </button>
                    )}
                  </div>

                  {/* Contenuto espanso */}
                  {isExpTitolo && (
                    <div className="bg-gray-50 border-t border-gray-100 px-5 py-3">
                      {/* Form aggiungi articolo */}
                      {isAddingHere && (
                        <div className="border border-dashed border-primary-300 rounded-lg p-3 mb-3 bg-white">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                            <div className="col-span-2">
                              <label className="block text-xs text-gray-400 mb-0.5">Articolo *</label>
                              <select
                                value={addFaseToTitoloArtId}
                                onChange={e => setAddFaseToTitoloArtId(e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white"
                              >
                                <option value="">Seleziona articolo...</option>
                                {articoliDisponibili.map(a => (
                                  <option key={a.id} value={a.id}>{a.ordine + 1}. {a.titolo}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-400 mb-0.5">Giorni</label>
                              <input
                                type="number"
                                min="1"
                                value={addFaseToTitoloDurata}
                                onChange={e => setAddFaseToTitoloDurata(parseInt(e.target.value) || 1)}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-400 mb-0.5">Tipo</label>
                              <select
                                value={addFaseToTitoloTipo}
                                onChange={e => setAddFaseToTitoloTipo(e.target.value as 'sequenziale' | 'parallelo')}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm bg-white"
                              >
                                <option value="sequenziale">Seq.</option>
                                <option value="parallelo">Par.</option>
                              </select>
                            </div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setAddingFaseToTitolo(null)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">Annulla</button>
                            <button
                              onClick={() => handleAddFaseToTitolo(titolo)}
                              disabled={!addFaseToTitoloArtId}
                              className="text-xs bg-primary-600 text-white px-3 py-1 rounded hover:bg-primary-700 disabled:opacity-40"
                            >Aggiungi</button>
                          </div>
                        </div>
                      )}

                      {/* Fasi (= articoli assegnati) */}
                      {fasesGroup.length === 0 ? (
                        <p className="text-xs text-gray-400 py-1">Nessun articolo assegnato a questa fase</p>
                      ) : (
                        <div className="space-y-1">
                          {fasesGroup.map(fase => {
                          const isExpFase = expandedFasi.has(fase.id)
                          const todayStr = new Date().toISOString().split('T')[0]
                          const artInfo = fase.articolo_id ? artMap[fase.articolo_id] : null

              return (
                <div key={fase.id} className="bg-white rounded border border-gray-100">
                  {/* Fase row */}
                  <div
                    className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => toggleExpandFase(fase.id)}
                  >
                    {isExpFase ? (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}

                    {/* Checkbox completata */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleToggleFaseCompletata(fase) }}
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        fase.completata
                          ? 'bg-green-500 border-green-500 text-white'
                          : fase.in_ritardo
                          ? 'border-red-400 hover:border-red-500'
                          : 'border-gray-300 hover:border-primary-500'
                      }`}
                    >
                      {fase.completata && <Check className="w-3 h-3" />}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {artInfo ? (
                          <span
                            className="flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded"
                            style={{ backgroundColor: artInfo.color.light, color: artInfo.color.text }}
                          >
                            A{artInfo.num} · {artInfo.titolo}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Senza articolo</span>
                        )}
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          fase.tipo === 'parallelo' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {fase.tipo === 'parallelo' ? 'Par.' : 'Seq.'}
                        </span>
                        {fase.in_ritardo && <AlertTriangle className="w-4 h-4 text-red-500" />}
                      </div>
                      <p className="text-xs text-gray-400">
                        {fase.responsabile_nome} · {fase.giorni_effettivi}/{fase.durata_prevista}gg
                        {fase.partecipanti_ids?.length > 0 && (
                          <span className="ml-2 text-blue-500">{fase.partecipanti_ids.length} pers.</span>
                        )}
                        {fase.strumenti_ids?.length > 0 && (
                          <span className="ml-1 text-amber-500">{fase.strumenti_ids.length} strum.</span>
                        )}
                        {(() => {
                          const costoFase = costiCalendario?.fasi?.find(cf => cf.fase_id === fase.id)
                          return costoFase && costoFase.costo_totale > 0 ? (
                            <span className="ml-2 text-green-600 font-medium">
                              {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(costoFase.costo_totale)}
                            </span>
                          ) : null
                        })()}
                        {fase.in_ritardo && <span className="text-red-500 ml-1">(in ritardo)</span>}
                      </p>
                    </div>

                    {/* Barra mini progress */}
                    <div className="w-20 flex-shrink-0">
                      <div className="bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${
                            fase.completata ? 'bg-green-500' :
                            fase.in_ritardo ? 'bg-red-500' :
                            fase.giorni_effettivi > 0 ? 'bg-blue-500' : 'bg-gray-300'
                          }`}
                          style={{ width: `${Math.min(100, fase.durata_prevista > 0 ? (fase.giorni_effettivi / fase.durata_prevista) * 100 : 0)}%` }}
                        />
                      </div>
                    </div>

                    {/* Azioni */}
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => setFaseAllegati({ id: fase.id, titolo: fase.titolo })}
                        className="p-1.5 text-gray-400 hover:text-amber-600 rounded transition-colors"
                        title="Documenti fase"
                      >
                        <Paperclip className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => startEditFase(fase)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 rounded transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => setConfirmAction({
                          title: 'Elimina Fase',
                          message: `Eliminare la fase "${fase.titolo}" e tutti i suoi giorni?`,
                          onConfirm: () => handleDeleteFase(fase.id)
                        })}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Giorni espansi */}
                  {isExpFase && (
                    <div className="bg-gray-50 border-t border-gray-100 px-4 py-3">
                      {/* Giorni list */}
                      {fase.giorni.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {fase.giorni.map(giorno => {
                            const isPast = giorno.data <= todayStr
                            return (
                              <div
                                key={giorno.id}
                                className={`flex items-start gap-3 p-2.5 rounded-lg text-sm ${
                                  isPast ? 'bg-white border border-green-200' : 'bg-white border border-gray-200'
                                }`}
                              >
                                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${isPast ? 'bg-green-500' : 'bg-blue-400'}`} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-gray-900">
                                      {formatDate(giorno.data)}
                                    </span>
                                    <span className={`text-xs ${isPast ? 'text-green-600' : 'text-blue-600'}`}>
                                      {isPast ? 'Svolto' : 'Previsto'}
                                    </span>
                                  </div>
                                  {giorno.partecipanti_ids.length > 0 && (
                                    <div className="flex items-center gap-1 flex-wrap mb-1">
                                      <Users className="w-3 h-3 text-gray-400" />
                                      {giorno.partecipanti_ids.map(pid => (
                                        <span key={pid} className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
                                          {getPersonaleNome(pid)}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {giorno.strumenti_ids.length > 0 && (
                                    <div className="flex items-center gap-1 flex-wrap">
                                      <Wrench className="w-3 h-3 text-gray-400" />
                                      {giorno.strumenti_ids.map(sid => (
                                        <span key={sid} className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">
                                          {getStrumentoNome(sid)}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {giorno.note && (
                                    <p className="text-xs text-gray-500 mt-1">{giorno.note}</p>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleDeleteGiorno(fase.id, giorno.id)}
                                  className="p-1 text-gray-400 hover:text-red-500 rounded flex-shrink-0"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      )}

                      {/* Add Giorno Form */}
                      {addingGiornoFaseId === fase.id ? (
                        <div className="border border-dashed border-primary-300 rounded-lg p-3 bg-white">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Data *</label>
                              <input
                                type="date"
                                value={giornoForm.data}
                                onChange={e => setGiornoForm(prev => ({ ...prev, data: e.target.value }))}
                                className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Note</label>
                              <input
                                type="text"
                                placeholder="Note opzionali..."
                                value={giornoForm.note}
                                onChange={e => setGiornoForm(prev => ({ ...prev, note: e.target.value }))}
                                className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                              />
                            </div>
                          </div>

                          {/* Partecipanti multi-select */}
                          <div className="mb-3">
                            <label className="block text-xs text-gray-500 mb-1">Partecipanti</label>
                            <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                              {personale.map(p => (
                                <button
                                  key={p.id}
                                  type="button"
                                  onClick={() => togglePartecipante(p.id)}
                                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                    giornoForm.partecipanti_ids.includes(p.id)
                                      ? 'bg-blue-100 border-blue-300 text-blue-700'
                                      : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300'
                                  }`}
                                >
                                  {p.cognome} {p.nome}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Strumenti multi-select */}
                          {strumenti.length > 0 && (
                            <div className="mb-3">
                              <label className="block text-xs text-gray-500 mb-1">Strumenti / Mezzi</label>
                              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                                {strumenti.map(s => (
                                  <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => toggleStrumento(s.id)}
                                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                      giornoForm.strumenti_ids.includes(s.id)
                                        ? 'bg-amber-100 border-amber-300 text-amber-700'
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-amber-300'
                                    }`}
                                  >
                                    {s.descrizione}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setAddingGiornoFaseId(null)}
                              className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                            >
                              Annulla
                            </button>
                            <button
                              onClick={() => handleCreateGiorno(fase.id)}
                              className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                            >
                              Aggiungi Giorno
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setAddingGiornoFaseId(fase.id)
                            setGiornoForm({
                              data: new Date().toISOString().split('T')[0],
                              partecipanti_ids: [],
                              strumenti_ids: [],
                              note: ''
                            })
                          }}
                          className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 py-1"
                        >
                          <Plus className="w-4 h-4" />
                          Aggiungi giorno di lavoro
                        </button>
                      )}

                      {/* Link Calendario */}
                      <button
                        onClick={() => navigate('/app/calendario')}
                        className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 py-1 mt-2"
                      >
                        <Calendar className="w-4 h-4" />
                        Vedi in Calendario
                      </button>

                      {/* Schede Dinamiche */}
                      <SchedePerEntita
                        entityType="fase_commessa"
                        entityId={fase.id}
                        entityLabel={`${commessa!.numero_commessa} - ${fase.titolo}`}
                      />
                    </div>
                  )}
                </div>
              )
              })}
                </div>
              )}
            </div>
          )}
        </div>
      )
      })}
    </div>
    )
    })()}
      </div>
      )}

      {/* =========== RENDICONTAZIONE ORE =========== */}
      {commessa && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Rendicontazione Ore
          </h2>
          <RendicontazionePanel
            commessaId={commessa.id}
            onToast={(msg, type) => setToast({ message: msg, type })}
          />
        </div>
      )}
    </div>
  )
}
